import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle2, XCircle, TrendingUp, Shield, Zap, Users, Globe, BarChart3, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  SECTION_IDS,
  scrollToSection,
  STATS,
  PROBLEM_POINTS,
  PILLARS,
  HOW_IT_WORKS_STEPS,
  MARKET_STATS,
  COMPARISON_ROWS,
  IMPACT_METRICS,
} from '@/lib/index';

// ─── Fade-in animation wrapper ────────────────────────────────────────────────
function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Section Label ─────────────────────────────────────────────────────────────
function SectionLabel({ text }: { text: string }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
      style={{
        background: 'rgba(124,58,237,0.12)',
        border: '1px solid rgba(124,58,237,0.25)',
      }}
    >
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#7C3AED', boxShadow: '0 0 6px #7C3AED' }} />
      <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#a78bfa' }}>{text}</span>
    </div>
  );
}

// ─── Particle Background ───────────────────────────────────────────────────────
function ParticleField() {
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 0.5,
    duration: Math.random() * 8 + 4,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: p.id % 3 === 0 ? '#7C3AED' : p.id % 3 === 1 ? '#2563EB' : '#06B6D4',
            boxShadow: `0 0 ${p.size * 3}px currentColor`,
          }}
          animate={{
            opacity: [0.2, 0.8, 0.2],
            scale: [1, 1.5, 1],
            y: [0, -20, 0],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

// ─── Glow Orb ──────────────────────────────────────────────────────────────────
function GlowOrb({ color, size, style }: { color: string; size: number; style?: React.CSSProperties }) {
  return (
    <div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        filter: 'blur(60px)',
        opacity: 0.15,
        ...style,
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1: HERO
// ─────────────────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section
      id={SECTION_IDS.HERO}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #050508 0%, #0a0a18 40%, #060614 100%)' }}
    >
      {/* Background Elements */}
      <ParticleField />
      <GlowOrb color="#7C3AED" size={800} style={{ top: '-20%', right: '-15%' }} />
      <GlowOrb color="#2563EB" size={600} style={{ bottom: '-10%', left: '-10%' }} />
      <GlowOrb color="#06B6D4" size={400} style={{ top: '40%', left: '40%' }} />

      {/* Gradient mesh */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 70% 50%, rgba(124,58,237,0.08) 0%, transparent 60%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* LEFT: Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                style={{
                  background: 'rgba(124,58,237,0.12)',
                  border: '1px solid rgba(124,58,237,0.3)',
                }}
              >
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#06B6D4', boxShadow: '0 0 8px #06B6D4' }} />
                <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#67e8f9' }}>Angola — Fintech do Futuro</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6"
            >
              <span className="text-white">Impulsionando</span>
              <br />
              <span
                style={{
                  background: 'linear-gradient(90deg, #7C3AED 0%, #2563EB 50%, #06B6D4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                a Economia
              </span>
              <br />
              <span className="text-white">Invisível</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="text-lg text-white/60 leading-relaxed mb-8 max-w-xl font-light"
            >
              Do dinheiro em espécie à inclusão financeira. KufutaPay+ transforma comerciantes informais de Angola em negócios financeiramente empoderados.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap gap-4 mb-12"
            >
              <button
                onClick={() => scrollToSection(SECTION_IDS.CTA)}
                className="group flex items-center gap-2 px-7 py-3.5 rounded-xl text-base font-semibold text-white transition-all duration-300 cursor-pointer relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                  boxShadow: '0 8px 30px rgba(124,58,237,0.5)',
                }}
              >
                Comece Agora
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection(SECTION_IDS.SOLUTION)}
                className="flex items-center gap-2 px-7 py-3.5 rounded-xl text-base font-medium text-white/80 hover:text-white transition-all duration-300 cursor-pointer"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                Ver a Solução
                <ChevronRight size={18} />
              </button>
            </motion.div>

            {/* Stats strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {STATS.slice(0, 2).map((stat) => (
                <div
                  key={stat.value}
                  className="p-4 rounded-xl"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div
                    className="text-2xl font-extrabold mb-1"
                    style={{
                      background: 'linear-gradient(90deg, #7C3AED, #2563EB)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-white/80 text-sm font-medium">{stat.label}</div>
                  <div className="text-white/40 text-xs font-light mt-0.5">{stat.description}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT: Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden"
              style={{
                boxShadow: '0 0 60px rgba(124,58,237,0.25), 0 40px 80px rgba(0,0,0,0.6)',
                border: '1px solid rgba(124,58,237,0.2)',
              }}
            >
              <img
                src="/images/hero_journey.png"
                alt="Jornada KufutaPay+ - comerciantes angolanos usando POS e app móvel em mercados de Luanda"
                className="w-full h-[480px] lg:h-[560px] object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0"
                style={{
                  background: 'linear-gradient(to top, rgba(5,5,15,0.7) 0%, transparent 50%)',
                }}
              />

              {/* Floating card: payment notification */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute bottom-6 left-6 right-6 p-4 rounded-xl"
                style={{
                  background: 'rgba(10,10,25,0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(124,58,237,0.3)',
                  boxShadow: '0 0 30px rgba(124,58,237,0.15)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)' }}
                  >
                    <CheckCircle2 size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold">Pagamento Recebido</div>
                    <div className="text-white/50 text-xs font-light">Via Multicaixa Express — 3.500 Kz</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold" style={{ color: '#4ade80' }}>+3.500 Kz</div>
                    <div className="text-white/30 text-xs font-light">agora</div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Floating badge */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute -top-5 -right-5 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{
                background: 'linear-gradient(135deg, #2563EB, #06B6D4)',
                boxShadow: '0 8px 24px rgba(37,99,235,0.5)',
              }}
            >
              PAGA FÁCIL
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #050508)' }}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2: O PROBLEMA
// ─────────────────────────────────────────────────────────────────────────────
function ProblemSection() {
  return (
    <section
      id={SECTION_IDS.PROBLEM}
      className="relative py-28 overflow-hidden"
      style={{ background: '#050508' }}
    >
      <GlowOrb color="#7C3AED" size={500} style={{ top: '20%', left: '-10%' }} />
      <GlowOrb color="#2563EB" size={400} style={{ bottom: '10%', right: '-5%' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeIn className="text-center mb-20">
          <SectionLabel text="O Problema" />
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-5 leading-tight">
            O sistema nunca foi<br />
            <span style={{ background: 'linear-gradient(90deg, #ef4444, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              feito para eles
            </span>
            <br />
            <span style={{ background: 'linear-gradient(90deg, #10b981, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Mas isso mudou.
            </span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Em Angola, mais de 80% da economia opera no sector informal. Milhões de comerciantes — do Roque Santeiro ao Mercado do Kikolo — agora têm acesso à identidade financeira digital através da KufutaPay+.
          </p>
        </FadeIn>

        {/* Split screen image */}
        <FadeIn className="mb-20">
          <div className="relative rounded-2xl overflow-hidden"
            style={{
              boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 40px 80px rgba(0,0,0,0.6)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <img
              src="/images/market_celebration.png"
              alt="Mercado angolano vibrante mostrando alegria e satisfação ao usar KufutaPay+ - transformação digital com drama e emoção"
              className="w-full h-[400px] lg:h-[500px] object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,5,15,0.6) 0%, transparent 40%)' }} />

            {/* Labels */}
            <div className="absolute top-6 left-6 px-3 py-1.5 rounded-lg"
              style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)' }}
            >
              <span className="text-red-300 text-xs font-semibold tracking-wider uppercase">Antes — Sem Acesso</span>
            </div>
            <div className="absolute top-6 right-6 px-3 py-1.5 rounded-lg"
              style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.3)' }}
            >
              <span className="text-emerald-300 text-xs font-semibold tracking-wider uppercase">Agora — Com KufutaPay+</span>
            </div>
          </div>
        </FadeIn>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {PROBLEM_POINTS.map((point, i) => (
            <FadeIn key={point.title} delay={i * 0.1}>
              <div
                className="p-7 rounded-2xl group hover:-translate-y-1 transition-all duration-300"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.2)' }}
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="rgba(248,113,113,1)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d={point.iconPath} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">{point.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed font-light">{point.description}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Stats band */}
        <FadeIn>
          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.04)' }}
          >
            {STATS.map((stat) => (
              <div
                key={stat.value}
                className="p-8 text-center"
                style={{ background: 'rgba(10,10,20,0.9)' }}
              >
                <div
                  className="text-4xl font-extrabold mb-2"
                  style={{
                    background: 'linear-gradient(90deg, #7C3AED, #2563EB, #06B6D4)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {stat.value}
                </div>
                <div className="text-white/80 text-sm font-semibold mb-1">{stat.label}</div>
                <div className="text-white/35 text-xs font-light leading-snug">{stat.description}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3: A SOLUÇÃO
// ─────────────────────────────────────────────────────────────────────────────
function SolutionSection() {
  return (
    <section
      id={SECTION_IDS.SOLUTION}
      className="relative py-28 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050508 0%, #07071a 50%, #050508 100%)' }}
    >
      <GlowOrb color="#2563EB" size={700} style={{ top: '0%', right: '-20%' }} />
      <GlowOrb color="#7C3AED" size={500} style={{ bottom: '20%', left: '-15%' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeIn className="text-center mb-20">
          <SectionLabel text="A Solução" />
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-5 leading-tight">
            Uma plataforma.{' '}
            <span style={{ background: 'linear-gradient(90deg, #7C3AED, #2563EB, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Três motores.
            </span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto font-light">
            KufutaPay+ integra pagamentos, crédito e inventário numa solução única construída para a realidade angolana.
          </p>
        </FadeIn>

        {/* Platform image */}
        <FadeIn className="mb-20">
          <div className="relative rounded-2xl overflow-hidden"
            style={{
              boxShadow: '0 0 60px rgba(124,58,237,0.2)',
              border: '1px solid rgba(124,58,237,0.15)',
            }}
          >
            <img
              src="/images/tech_platform.png"
              alt="Plataforma KufutaPay+ com POS Sunmi e visualização holográfica de dados"
              className="w-full h-[360px] lg:h-[440px] object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(7,7,26,0.8) 0%, transparent 50%)' }} />
          </div>
        </FadeIn>

        {/* 3 Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          {PILLARS.map((pillar, i) => (
            <FadeIn key={pillar.number} delay={i * 0.15}>
              <div
                className="relative p-8 rounded-2xl h-full flex flex-col group hover:-translate-y-2 transition-all duration-400 cursor-default"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: '0 4px 30px rgba(0,0,0,0.3)',
                }}
              >
                {/* Glow on hover */}
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                  style={{ boxShadow: `0 0 40px ${pillar.glowColor}, inset 0 0 40px ${pillar.glowColor.replace('0.4', '0.05')}` }}
                />

                {/* Number */}
                <div
                  className="text-6xl font-black mb-6 leading-none opacity-10"
                  style={{
                    background: `linear-gradient(135deg, ${pillar.color.replace('from-', '#').replace(' to-', ', #').replace(/-\d+/g, '')} )`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {pillar.number}
                </div>

                <div
                  className={`inline-flex w-12 h-12 rounded-xl items-center justify-center mb-5 bg-gradient-to-br ${pillar.color}`}
                  style={{ boxShadow: `0 4px 20px ${pillar.glowColor}` }}
                >
                  {i === 0 && <Zap size={22} className="text-white" />}
                  {i === 1 && <BarChart3 size={22} className="text-white" />}
                  {i === 2 && <TrendingUp size={22} className="text-white" />}
                </div>

                <h3 className="text-white text-2xl font-bold mb-2">{pillar.title}</h3>
                <p className="text-white/40 text-xs font-medium tracking-wider uppercase mb-4">{pillar.subtitle}</p>
                <p className="text-white/55 text-sm leading-relaxed font-light mb-6 flex-1">{pillar.description}</p>

                <ul className="space-y-2.5">
                  {pillar.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2.5">
                      <CheckCircle2 size={15} className="flex-shrink-0" style={{ color: '#06B6D4' }} />
                      <span className="text-white/65 text-sm font-light">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* PAGA FÁCIL feature callout */}
        <FadeIn>
          <div
            className="relative p-8 lg:p-12 rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(37,99,235,0.12) 50%, rgba(6,182,212,0.08) 100%)',
              border: '1px solid rgba(124,58,237,0.2)',
              boxShadow: '0 0 60px rgba(124,58,237,0.1)',
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(124,58,237,0.1) 0%, transparent 60%)',
              }}
            />
            <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                  style={{ background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.3)' }}
                >
                  <Shield size={12} style={{ color: '#06B6D4' }} />
                  <span className="text-xs font-semibold tracking-wider uppercase" style={{ color: '#67e8f9' }}>Recurso Exclusivo</span>
                </div>
                <h3
                  className="text-3xl lg:text-4xl font-extrabold mb-4"
                  style={{
                    background: 'linear-gradient(90deg, #67e8f9, #a78bfa)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  PAGA FÁCIL
                </h3>
                <p className="text-white/60 leading-relaxed mb-6 font-light">
                  Clientes do comerciante geram comprovativo de pagamento via app móvel. Pagam sem dinheiro físico, directamente ao vendedor. A ponte definitiva entre o offline e o digital em Angola.
                </p>
                <div className="space-y-3">
                  {[
                    'Cliente gera QR Code no app PAGA FÁCIL',
                    'Vendedor confirma o pagamento no POS',
                    'Transacção registada e historial actualizado',
                    'Sem necessidade de dinheiro físico',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(6,182,212,0.2)', border: '1px solid rgba(6,182,212,0.4)' }}
                      >
                        <CheckCircle2 size={12} style={{ color: '#06B6D4' }} />
                      </div>
                      <span className="text-white/65 text-sm font-light">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mock phone UI */}
              <div className="flex justify-center lg:justify-end">
                <div
                  className="relative w-64 rounded-3xl overflow-hidden p-1"
                  style={{
                    background: 'linear-gradient(135deg, rgba(124,58,237,0.5) 0%, rgba(6,182,212,0.3) 100%)',
                    boxShadow: '0 0 60px rgba(6,182,212,0.3)',
                  }}
                >
                  <div className="rounded-2xl p-5" style={{ background: '#0a0a1a' }}>
                    <div className="text-center mb-4">
                      <div className="text-white/40 text-xs font-light mb-1">PAGA FÁCIL</div>
                      <div className="text-white text-lg font-bold">Gerar Pagamento</div>
                    </div>
                    {/* QR Mock */}
                    <div
                      className="w-full aspect-square rounded-xl flex items-center justify-center mb-4"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <div className="grid grid-cols-5 gap-1 p-4 w-full">
                        {Array.from({ length: 25 }, (_, i) => (
                          <div
                            key={i}
                            className="aspect-square rounded-sm"
                            style={{
                              background: [0, 1, 2, 5, 10, 12, 14, 19, 22, 23, 24, 6, 7, 8, 9, 15, 16, 17, 18].includes(i)
                                ? 'rgba(124,58,237,0.9)' : 'rgba(255,255,255,0.04)',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white mb-1">5.000 Kz</div>
                      <div className="text-white/40 text-xs font-light">Válido por 2 minutos</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4: COMO FUNCIONA
// ─────────────────────────────────────────────────────────────────────────────
function HowItWorksSection() {
  return (
    <section
      id={SECTION_IDS.HOW_IT_WORKS}
      className="relative py-28 overflow-hidden"
      style={{ background: '#050508' }}
    >
      <GlowOrb color="#06B6D4" size={600} style={{ top: '10%', right: '-10%' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeIn className="text-center mb-20">
          <SectionLabel text="Jornada do Comerciante" />
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-5 leading-tight">
            Como Funciona
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto font-light">
            Da barraca à prosperidade. Cinco passos que transformam a vida de um comerciante angolano.
          </p>
        </FadeIn>

        <div className="relative">
          {/* Connecting line */}
          <div
            className="absolute left-1/2 top-8 bottom-8 w-px hidden lg:block"
            style={{
              background: 'linear-gradient(to bottom, transparent, rgba(124,58,237,0.3) 20%, rgba(6,182,212,0.3) 80%, transparent)',
            }}
          />

          <div className="space-y-6">
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <FadeIn key={step.step} delay={i * 0.1}>
                <div className={`flex items-center gap-8 lg:gap-16 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Content Card */}
                  <div
                    className="flex-1 p-7 rounded-2xl group hover:-translate-y-1 transition-all duration-300"
                    style={{
                      background: 'rgba(255,255,255,0.025)',
                      border: '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    <div className="flex items-start gap-5">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: 'linear-gradient(135deg, #7C3AED, #2563EB)',
                          boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
                        }}
                      >
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d={step.iconPath} />
                        </svg>
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className="text-xs font-bold tracking-widest uppercase px-2 py-0.5 rounded"
                            style={{ color: '#a78bfa', background: 'rgba(124,58,237,0.15)' }}
                          >
                            Passo {step.step}
                          </span>
                        </div>
                        <h3 className="text-white font-semibold text-xl mb-2">{step.title}</h3>
                        <p className="text-white/50 text-sm leading-relaxed font-light">{step.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Center node */}
                  <div className="hidden lg:flex flex-col items-center flex-shrink-0">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg"
                      style={{
                        background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
                        boxShadow: '0 0 25px rgba(124,58,237,0.6)',
                      }}
                    >
                      {step.step}
                    </div>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 hidden lg:block" />
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5: MERCADO
// ─────────────────────────────────────────────────────────────────────────────
function MarketSection() {
  return (
    <section
      id={SECTION_IDS.MARKET}
      className="relative py-28 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050508 0%, #07071a 50%, #050508 100%)' }}
    >
      <GlowOrb color="#7C3AED" size={700} style={{ top: '20%', right: '-20%' }} />
      <GlowOrb color="#2563EB" size={500} style={{ bottom: '10%', left: '-15%' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeIn className="text-center mb-20">
          <SectionLabel text="Oportunidade de Mercado" />
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-5 leading-tight">
            O maior mercado{' '}
            <span style={{ background: 'linear-gradient(90deg, #7C3AED, #2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              não servido
            </span>
            <br />do mundo
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto font-light">
            A economia informal africana é massiva, crescente e quase completamente ignorada pelo sistema financeiro tradicional.
          </p>
        </FadeIn>

        {/* Map visualization */}
        <FadeIn className="mb-20">
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              boxShadow: '0 0 60px rgba(124,58,237,0.2)',
              border: '1px solid rgba(124,58,237,0.15)',
            }}
          >
            <img
              src="/images/africa_map.png"
              alt="Mapa holográfico de África mostrando oportunidade de mercado fintech"
              className="w-full h-[400px] lg:h-[480px] object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(7,7,26,0.7) 0%, transparent 40%)' }} />

            {/* Floating data badges */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-8 left-8 p-4 rounded-xl"
              style={{
                background: 'rgba(10,10,25,0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(124,58,237,0.3)',
              }}
            >
              <div className="text-xs text-white/40 font-light mb-1 uppercase tracking-wider">Angola — Informal</div>
              <div className="text-2xl font-extrabold" style={{ color: '#a78bfa' }}>+$25B</div>
              <div className="text-white/40 text-xs font-light">economia por servir</div>
            </motion.div>

            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute top-8 right-8 p-4 rounded-xl"
              style={{
                background: 'rgba(10,10,25,0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(6,182,212,0.3)',
              }}
            >
              <div className="text-xs text-white/40 font-light mb-1 uppercase tracking-wider">África — Fintech 2030</div>
              <div className="text-2xl font-extrabold" style={{ color: '#67e8f9' }}>$65B</div>
              <div className="text-white/40 text-xs font-light">crescimento 13x</div>
            </motion.div>
          </div>
        </FadeIn>

        {/* Market Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {MARKET_STATS.map((stat, i) => (
            <FadeIn key={stat.value} delay={i * 0.1}>
              <div
                className="p-7 rounded-2xl text-center group hover:-translate-y-1 transition-all duration-300"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                }}
              >
                <div
                  className="text-4xl font-extrabold mb-3"
                  style={{
                    background: `linear-gradient(90deg, ${
                      i === 0 ? '#7C3AED, #a78bfa' :
                      i === 1 ? '#2563EB, #60a5fa' :
                      i === 2 ? '#06B6D4, #67e8f9' :
                      '#10b981, #34d399'
                    })`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {stat.value}
                </div>
                <div className="text-white/80 text-sm font-semibold mb-1">{stat.label}</div>
                <div className="text-white/35 text-xs font-light">{stat.subtext}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 6: DIFERENCIAÇÃO
// ─────────────────────────────────────────────────────────────────────────────
function DifferentiationSection() {
  return (
    <section
      id={SECTION_IDS.DIFFERENTIATION}
      className="relative py-28 overflow-hidden"
      style={{ background: '#050508' }}
    >
      <GlowOrb color="#06B6D4" size={500} style={{ top: '30%', left: '-10%' }} />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeIn className="text-center mb-20">
          <SectionLabel text="Diferenciais" />
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-5 leading-tight">
            Porque KufutaPay+{' '}
            <span style={{ background: 'linear-gradient(90deg, #7C3AED, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              é diferente
            </span>
          </h2>
          <p className="text-white/50 text-lg max-w-xl mx-auto font-light">
            Não somos mais uma carteira digital. Somos a infraestrutura financeira completa para o comerciante informal angolano.
          </p>
        </FadeIn>

        <FadeIn>
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              border: '1px solid rgba(255,255,255,0.07)',
              boxShadow: '0 0 60px rgba(0,0,0,0.4)',
            }}
          >
            {/* Header row */}
            <div
              className="grid grid-cols-3 px-8 py-5"
              style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="text-white/40 text-sm font-semibold">Funcionalidade</div>
              <div className="text-center">
                <span className="text-white/40 text-sm font-semibold">Outros</span>
              </div>
              <div className="text-center">
                <span
                  className="text-sm font-bold px-3 py-1 rounded-lg"
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED, #2563EB)',
                    color: 'white',
                  }}
                >
                  KufutaPay+
                </span>
              </div>
            </div>

            {/* Data rows */}
            {COMPARISON_ROWS.map((row, i) => (
              <div
                key={row.feature}
                className="grid grid-cols-3 px-8 py-5 group transition-colors duration-200"
                style={{
                  background: i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent',
                  borderBottom: i < COMPARISON_ROWS.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                }}
              >
                <div className="text-white/70 text-sm font-light flex items-center">{row.feature}</div>
                <div className="flex items-center justify-center">
                  {row.others ? (
                    <CheckCircle2 size={18} className="text-white/25" />
                  ) : (
                    <XCircle size={18} style={{ color: 'rgba(239,68,68,0.5)' }} />
                  )}
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle2 size={18} style={{ color: '#4ade80' }} />
                </div>
              </div>
            ))}
          </div>
        </FadeIn>

        {/* Tagline */}
        <FadeIn className="text-center mt-14">
          <p className="text-white/40 text-base font-light">
            Pagamentos + Crédito + Stock — construído para o B2B informal angolano.{' '}
            <span
              className="font-semibold cursor-pointer hover:opacity-80 transition-opacity"
              style={{ color: '#a78bfa' }}
              onClick={() => scrollToSection(SECTION_IDS.CTA)}
            >
              Saiba mais
            </span>
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 7: IMPACTO
// ─────────────────────────────────────────────────────────────────────────────
function ImpactSection() {
  return (
    <section
      id={SECTION_IDS.IMPACT}
      className="relative py-28 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050508 0%, #07071a 50%, #050508 100%)' }}
    >
      <GlowOrb color="#10b981" size={500} style={{ top: '20%', right: '-10%' }} />
      <GlowOrb color="#7C3AED" size={400} style={{ bottom: '10%', left: '-5%' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeIn className="text-center mb-20">
          <SectionLabel text="Impacto Social — ESG" />
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-5 leading-tight">
            Mais do que fintech.{' '}
            <span style={{ background: 'linear-gradient(90deg, #10b981, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Impacto real.
            </span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto font-light">
            KufutaPay+ está alinhado com os Objectivos de Desenvolvimento Sustentável das Nações Unidas. Cada transacção contribui para uma Angola mais equitativa.
          </p>
        </FadeIn>

        {/* Impact image */}
        <FadeIn className="mb-20">
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              boxShadow: '0 0 60px rgba(16,185,129,0.15)',
              border: '1px solid rgba(16,185,129,0.1)',
            }}
          >
            <img
              src="/images/impact_success.png"
              alt="Empreendedora angolana de sucesso usando KufutaPay+ no seu negócio em Luanda"
              className="w-full h-[400px] lg:h-[460px] object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(7,7,26,0.75) 0%, transparent 50%)' }} />
            <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
              <div className="text-white/40 text-sm font-light mb-2 uppercase tracking-widest">Testemunho</div>
              <blockquote className="text-white text-xl lg:text-2xl font-light italic leading-relaxed max-w-2xl">
                "Antes eu não existia para o banco. Hoje tenho crédito, stock e clientes pagam pelo telemóvel."
              </blockquote>
              <div className="mt-4 text-white/40 text-sm font-light">— Comerciante, Mercado do Benfica, Luanda</div>
            </div>
          </div>
        </FadeIn>

        {/* SDG Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {IMPACT_METRICS.map((metric, i) => {
            const sdgImages = [
              '/images/sdg_individual_2.png', // ODS 1
              '/images/sdg_work_2.png',       // ODS 8
              '/images/sdg_innovation_1.png', // ODS 9
              '/images/sdg_inequality_1.jpeg' // ODS 10
            ];
            
            return (
              <FadeIn key={metric.sdg} delay={i * 0.1}>
                <div
                  className="p-7 rounded-2xl group hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={sdgImages[i]}
                      alt={`${metric.sdg} - ${metric.title}`}
                      className="w-12 h-12 object-contain rounded-lg"
                      style={{
                        filter: 'brightness(1.1) contrast(1.1)',
                      }}
                    />
                    <div
                      className="px-3 py-1 rounded-lg text-xs font-bold"
                      style={{ 
                        background: `linear-gradient(135deg, ${metric.color.replace('from-', '').replace(' to-', ', ')})`,
                        color: 'white' 
                      }}
                    >
                      {metric.sdg}
                    </div>
                  </div>
                  <h3 className="text-white font-semibold text-base mb-3">{metric.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed font-light flex-1">{metric.description}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>

        {/* ESG summary strip */}
        <FadeIn>
          <div
            className="grid grid-cols-3 divide-x overflow-hidden rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {[
              { icon: Users, label: 'Inclusão Financeira', value: '12M+', desc: 'comerciantes alcançáveis' },
              { icon: Globe, label: 'Cobertura', value: '18', desc: 'províncias de Angola' },
              { icon: TrendingUp, label: 'Crescimento de PMEs', value: '3×', desc: 'receitas após adopção' },
            ].map((item, i) => (
              <div key={i} className="p-8 text-center" style={{ borderRight: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <item.icon size={24} className="mx-auto mb-3" style={{ color: '#a78bfa' }} />
                <div
                  className="text-3xl font-extrabold mb-1"
                  style={{
                    background: 'linear-gradient(90deg, #7C3AED, #06B6D4)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {item.value}
                </div>
                <div className="text-white/70 text-sm font-semibold mb-0.5">{item.label}</div>
                <div className="text-white/35 text-xs font-light">{item.desc}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 8: CTA FINAL
// ─────────────────────────────────────────────────────────────────────────────
function CtaSection() {
  return (
    <section
      id={SECTION_IDS.CTA}
      className="relative py-32 overflow-hidden"
      style={{ background: '#050508' }}
    >
      <ParticleField />
      <GlowOrb color="#7C3AED" size={900} style={{ top: '-30%', left: '50%', transform: 'translateX(-50%)' }} />
      <GlowOrb color="#2563EB" size={600} style={{ bottom: '-20%', right: '10%' }} />

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 80% at 50% 50%, rgba(124,58,237,0.08) 0%, transparent 65%)',
        }}
      />

      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
        <FadeIn>
          <SectionLabel text="Junte-se à Revolução" />
        </FadeIn>

        <FadeIn delay={0.1}>
          <h2
            className="text-5xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-tight"
            style={{
              background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 40%, #a78bfa 70%, #67e8f9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Junte-se à Revolução Financeira da África
          </h2>
        </FadeIn>

        <FadeIn delay={0.2}>
          <p className="text-white/55 text-xl leading-relaxed mb-12 font-light max-w-2xl mx-auto">
            Seja um parceiro, investidor ou comerciante. Juntos, estamos a construir a infraestrutura financeira que Angola merece.
          </p>
        </FadeIn>

        <FadeIn delay={0.3}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              className="group w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-base font-semibold text-white transition-all duration-300 cursor-pointer relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                boxShadow: '0 8px 40px rgba(124,58,237,0.55)',
              }}
            >
              <span className="relative z-10">Solicitar Demonstração</span>
              <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'linear-gradient(135deg, #6D28D9 0%, #1D4ED8 100%)' }}
              />
            </button>

            <button
              className="group w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-base font-medium text-white/80 hover:text-white transition-all duration-300 cursor-pointer"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              }}
            >
              Seja Nosso Parceiro
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </FadeIn>

        {/* Trust indicators */}
        <FadeIn delay={0.4}>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {[
              { icon: Shield, text: 'Regulado pelo BNA' },
              { icon: CheckCircle2, text: 'ISO 27001 Certificado' },
              { icon: Globe, text: 'Presença Nacional' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <item.icon size={16} className="text-white/30" />
                <span className="text-white/30 text-sm font-light">{item.text}</span>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOME PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  // Cursor glow effect
  const [cursor, setCursor] = useState({ x: -200, y: -200 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursor({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ background: '#050508', color: 'white', fontFamily: 'Inter, sans-serif' }}
    >
      {/* Global cursor glow */}
      <div
        className="fixed pointer-events-none z-0 rounded-full transition-all duration-500"
        style={{
          width: 600,
          height: 600,
          left: cursor.x - 300,
          top: cursor.y - 300,
          background: 'radial-gradient(circle, rgba(124,58,237,0.04) 0%, transparent 65%)',
        }}
      />

      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <HowItWorksSection />
        <MarketSection />
        <DifferentiationSection />
        <ImpactSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
